import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { navigate } from '../App';
import { PLAN_CATALOG, normalizePlan, planHas, assignedPlan } from '../plans';
import { BrandMark, BrandWordmark } from '../components/BrandIdentity';
import ProductIcon from '../components/ProductIcon';
import ProblemReport from '../components/ProblemReport';
import UsageBillingPanel from '../components/UsageBillingPanel';
import { signOutRecover, supabase } from '../supabase';
import useReservationRealtime from '../useReservationRealtime';
import RecoverBookings from './RecoverBookings';
import AiActivation from '../components/AiActivation';
import OperationsPanel from './OperationsPanel';
import RestaurantContent from './RestaurantContent';
import GuestInbox from './GuestInbox';

const EMPTY_SETTINGS = {
  enabled: false,
  booking_mode: 'native',
  average_cover_value: 35,
  booking_duration_minutes: 120,
  max_party_size: 50,
  booking_window_days: 60,
  min_notice_minutes: 60,
  cancellation_notice_minutes: 120,
  public_booking_enabled: true,
  public_booking_intro: '',
  provisioning_status: 'pending',
  plan: 'free',
  message_allowance: 0,
  messages_used: 0,
  phone_number_assigned: null,
  sms_notifications_enabled: false,
};

function dateString(date) {
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 10);
}

function publicBaseUrl() {
  return (process.env.REACT_APP_SITE_URL || window.location.origin).replace(/\/$/, '');
}

function useCompactNavigation() {
  const [compact, setCompact] = useState(() => window.matchMedia?.('(max-width: 700px)').matches ?? false);
  useEffect(() => {
    const media = window.matchMedia?.('(max-width: 700px)');
    if (!media) return undefined;
    const update = () => setCompact(media.matches);
    update();
    media.addEventListener('change', update);
    return () => media.removeEventListener('change', update);
  }, []);
  return compact;
}

export default function RecoverDashboard({ session, onLogout, initialView = 'bookings' }) {
  const compactNavigation = useCompactNavigation();
  const [restaurant, setRestaurant] = useState(null);
  const [settings, setSettings] = useState(EMPTY_SETTINGS);
  const [calls, setCalls] = useState([]);
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState('');
  const [activeView, setActiveView] = useState(() => new URLSearchParams(window.location.search).has('ai_checkout') ? 'activation' : initialView === 'menu' ? 'menu' : 'bookings');
  const [calendarTab, setCalendarTab] = useState('agenda');
  const [moreOpen, setMoreOpen] = useState(false);
  const moreButton = useRef(null);
  const pageHeading = useRef(null);
  const [commerce, setCommerce] = useState({ subscription: { plan: 'free', status: 'manual' } });
  const [configuration, setConfiguration] = useState({ rooms: 0, tables: 0, shifts: 0 });

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    const { data: restaurantRow } = await supabase
      .from('ristoranti')
      .select('id,nome,citta,telefono,slug')
      .eq('user_id', session.user.id)
      .maybeSingle();
    if (!restaurantRow) {
      try {
        const response = await fetch('/api/founder?action=authorize', {
          headers: { Authorization: `Bearer ${session.access_token}` },
        });
        if (response.ok) {
          window.location.replace('https://founder.ristodaily.com');
          return;
        }
      } catch { /* The fallback below still lets the user leave the wrong account. */ }
      if (!silent) setLoading(false);
      return;
    }
    setRestaurant(restaurantRow);
    const fromDate = new Date(); fromDate.setDate(fromDate.getDate() - 30);
    const [{ data: settingsRow }, { data: callRows }, { data: reservationRows }, { count: rooms }, { count: tables }, { count: shifts }] = await Promise.all([
      supabase.from('recover_settings').select('*').eq('ristorante_id', restaurantRow.id).maybeSingle(),
      supabase.from('recover_calls').select('id,status,caller_phone_last4,created_at,link_opened_at,converted_at,estimated_value').eq('ristorante_id', restaurantRow.id).gte('created_at', fromDate.toISOString()).order('created_at', { ascending: false }).limit(500),
      supabase.from('recover_reservations').select('id,booking_date,party_size,status,source,created_at').eq('ristorante_id', restaurantRow.id).gte('created_at', fromDate.toISOString()).order('created_at'),
      supabase.from('recover_rooms').select('id', { count: 'exact', head: true }).eq('ristorante_id', restaurantRow.id),
      supabase.from('recover_tables').select('id', { count: 'exact', head: true }).eq('ristorante_id', restaurantRow.id).eq('attivo', true),
      supabase.from('recover_shifts').select('id', { count: 'exact', head: true }).eq('ristorante_id', restaurantRow.id).eq('attivo', true),
    ]);
    setSettings({ ...EMPTY_SETTINGS, ...(settingsRow || {}) });
    setCalls(callRows || []);
    setReservations(reservationRows || []);
    setConfiguration({ rooms: rooms || 0, tables: tables || 0, shifts: shifts || 0 });
    if (normalizePlan(settingsRow?.plan) !== 'free') {
      try {
        const response = await fetch('/api/billing?action=status', { headers: { Authorization: `Bearer ${session.access_token}` } });
        if (response.ok) setCommerce(await response.json());
      } catch { /* Billing is optional while offline. */ }
    } else {
      setCommerce({ subscription: { plan: 'free', status: 'manual' } });
    }
    if (!silent) setLoading(false);
  }, [session.user.id, session.access_token]);

  useEffect(() => { load(); }, [load]);

  const refreshDashboard = useCallback(() => load(true), [load]);
  useReservationRealtime('dashboard', activeView === 'bookings' ? null : restaurant?.id, refreshDashboard);

  const activePlan = normalizePlan(settings.plan);
  const hasPaidService = activePlan !== 'free';
  const canUseCalls = planHas(activePlan, 'calls');
  const secondaryNavigation = [
    ...(canUseCalls ? [['calls', 'Chiamate']] : []),
    ['overview', 'Risultati'],
    ['activation', 'Assistente AI'],
  ];
  const moreNavigation = [
    ...(compactNavigation ? secondaryNavigation : []),
    ['settings', 'Impostazioni'],
    ...(hasPaidService ? [['billing', 'Piano e consumi'], ['operations', 'Verifica prenotazioni e SMS']] : []),
  ];
  useEffect(() => {
    if ((!hasPaidService && ['calls', 'billing', 'operations'].includes(activeView)) || activeView === 'voice') setActiveView('menu');
  }, [activeView, hasPaidService]);

  const metrics = useMemo(() => buildMetrics(reservations, Number(settings.average_cover_value || 0), canUseCalls), [reservations, settings.average_cover_value, canUseCalls]);
  const bookingUrl = restaurant?.slug ? `${publicBaseUrl()}/prenota/${restaurant.slug}` : '';

  function goTo(view, tab = 'agenda') {
    setCalendarTab(tab);
    setActiveView(view);
    setMoreOpen(false);
    window.requestAnimationFrame(() => pageHeading.current?.focus());
  }

  async function saveSettings() {
    if (!restaurant) return;
    setSaving(true); setNotice('');
    const payload = {
      public_booking_enabled: Boolean(settings.public_booking_enabled),
      public_booking_intro: settings.public_booking_intro?.trim() || null,
      average_cover_value: Number(settings.average_cover_value) || 35,
      booking_duration_minutes: Number(settings.booking_duration_minutes) || 120,
      max_party_size: Number(settings.max_party_size) || 50,
      booking_window_days: Number(settings.booking_window_days) || 60,
      min_notice_minutes: Number(settings.min_notice_minutes) || 60,
      cancellation_notice_minutes: Number(settings.cancellation_notice_minutes) || 0,
      ...(planHas(activePlan, 'sms') ? { sms_notifications_enabled: Boolean(settings.sms_notifications_enabled) } : {}),
    };
    const { data, error } = await supabase.from('recover_settings').update(payload).eq('ristorante_id', restaurant.id).select('*').single();
    if (error) setNotice('Non siamo riusciti a salvare. Riprova tra poco.');
    else {
      setSettings({ ...EMPTY_SETTINGS, ...data });
      setNotice('Impostazioni salvate.');
    }
    setSaving(false);
  }

  async function logout() { await signOutRecover(); onLogout(); }

  if (loading) return <div className="app-loader"><span /></div>;
  if (!restaurant) return <main className="empty-account"><div className="glass-card"><h1>Account non collegato a un ristorante.</h1><p>Hai effettuato l’accesso come <strong>{session.user.email}</strong>. Se hai appena creato l’account puoi aggiornare la pagina; altrimenti esci e usa l’account del ristorante.</p><div className="empty-account-actions"><button className="button button-ghost" onClick={() => window.location.reload()}>Aggiorna</button><button className="button" onClick={logout}>Esci e cambia account</button></div></div></main>;

  return (
    <div className="recover-app">
      <aside className="app-sidebar glass-card">
        <button className="brand" onClick={() => navigate('/')}><BrandWordmark /></button>
        <div className="restaurant-chip"><span>{restaurant.nome?.slice(0, 1).toUpperCase()}</span><div><strong>{restaurant.nome}</strong><small>{restaurant.citta || 'Italia'}</small></div></div>
        <nav aria-label="Menu del ristorante" onKeyDown={(event) => {
          if (event.key === 'Escape' && moreOpen) { setMoreOpen(false); moreButton.current?.focus(); }
        }}>
          {[
            ['menu', 'Menù'], ['bookings', 'Agenda'], ['guests', 'Ospiti'],
            ...(!compactNavigation ? secondaryNavigation : []),
          ].map(([view, label]) => <button key={view} className={activeView === view ? 'active' : ''} aria-current={activeView === view ? 'page' : undefined} aria-label={label} title={label} onClick={() => goTo(view)}><i><ProductIcon name={view} /></i><span className="rd-nav-label">{label}</span></button>)}
          <button ref={moreButton} className={moreOpen || moreNavigation.some(([view]) => view === activeView) ? 'active' : ''} aria-label="Altro" aria-expanded={moreOpen} aria-controls="restaurant-more" onClick={() => setMoreOpen(!moreOpen)}><i><ProductIcon name="settings" /></i><span className="rd-nav-label">Altro</span></button>
          <div id="restaurant-more" className="rd-more-menu" hidden={!moreOpen}>
            <p>Gestisci il ristorante</p>
            {moreNavigation.map(([view, label]) => <button key={view} className={activeView === view ? 'active' : ''} aria-current={activeView === view ? 'page' : undefined} onClick={() => goTo(view)}><i><ProductIcon name={view} /></i><span>{label}</span></button>)}
          </div>
        </nav>
        <div className="sidebar-bottom"><div className="plan-chip"><span>Piano</span><strong>{assignedPlan(settings).name}</strong></div><button onClick={logout}>Esci</button></div>
      </aside>

      <main className="app-main">
        <div className="rd-compact-topbar"><BrandWordmark /><button type="button" onClick={logout} aria-label="Esci dall’account"><ProductIcon name="logout" /></button></div>
        <header className="app-header">
          <div><p className="eyebrow">{restaurant.nome}</p><h1 ref={pageHeading} tabIndex="-1">{viewTitle(activeView)}</h1><p className="rd-view-hint">{viewDescription(activeView)}</p></div>
          {activeView !== 'menu' && <StatusBadge enabled={settings.public_booking_enabled} />}
        </header>
        <ProblemReport section={viewTitle(activeView)} session={session} />

        {activeView === 'settings' && bookingUrl && <DirectLinkCard url={bookingUrl} enabled={settings.public_booking_enabled} onOpenSettings={() => goTo('settings')} />}
        {['overview', 'bookings'].includes(activeView) && <OnboardingChecklist settings={settings} configuration={configuration} bookingUrl={bookingUrl} onGo={goTo} />}
{activeView === 'overview' && <Overview callsCount={calls.length} metrics={metrics} averageCover={Number(settings.average_cover_value || 0)} showRecover={canUseCalls} />}
        {activeView === 'calls' && canUseCalls && <CallsView calls={calls} settings={settings} onActivate={() => goTo('activation')} onCheck={() => goTo('operations')} />}
        {activeView === 'bookings' && <><div className="rd-agenda-links"><button className="auth-forgot" onClick={() => goTo('settings')}>Condividi il link prenotazioni</button><button className="auth-forgot" onClick={() => goTo('guests')}>Richieste degli ospiti e lista d’attesa →</button></div><RecoverBookings initialTab={calendarTab} restaurantId={restaurant.id} settings={settings} onSettingsChange={(next) => setSettings({ ...EMPTY_SETTINGS, ...next })} /></>}
        {activeView === 'settings' && <SettingsPanel settings={settings} setSettings={setSettings} saving={saving} notice={notice} bookingUrl={bookingUrl} onSave={saveSettings} onActivate={() => goTo('activation')} />}
        {activeView === 'activation' && <AiActivation session={session} existingService={hasPaidService} />}
        {activeView === 'operations' && hasPaidService && <OperationsPanel session={session} />}
        {activeView === 'menu' && <RestaurantContent session={session} restaurantName={restaurant?.nome} />}
        {activeView === 'guests' && <GuestInbox session={session} />}
        {activeView === 'billing' && hasPaidService && <BillingPanel session={session} commerce={commerce} settings={settings} onActivate={() => goTo('activation')} />}
      </main>
    </div>
  );
}

function viewTitle(view) {
  if (view === 'operations') return 'Verifica prenotazioni e SMS';
  if (view === 'menu') return 'Il tuo menù';
  if (view === 'guests') return 'Richieste speciali e lista d’attesa';
  return ({ overview: 'Risultati del ristorante', bookings: 'Agenda e disponibilità', calls: 'Chiamate del ristorante', settings: 'Impostazioni prenotazioni', activation: 'Attivazione telefono', billing: 'Piano e consumi' })[view];
}

function viewDescription(view) {
  return ({
    bookings: 'Il servizio di oggi, i tavoli e le prossime prenotazioni.',
    guests: 'Controlla le esigenze segnalate e chi aspetta un tavolo.',
    calls: 'Consulta gli esiti registrati e verifica eventuali SMS mancanti.',
    overview: 'Prenotazioni e provenienza degli ultimi 30 giorni.',
    settings: 'Link pubblico e regole per ricevere prenotazioni.',
    activation: 'Dati e documenti per il collegamento assistito del telefono.',
    menu: 'Organizza i piatti e controlla come li vedranno gli ospiti.',
    billing: 'Il piano assegnato e i consumi. Gli addebiti restano disattivati.',
    operations: 'Controlla separatamente il salvataggio del tavolo e l’esito del messaggio.',
  })[view];
}

function OnboardingChecklist({ settings, configuration, bookingUrl, onGo }) {
  const items = [
    { label: 'Link pubblico attivo', done: Boolean(settings.public_booking_enabled && bookingUrl), view: 'settings' },
    { label: 'Configura sale e tavoli', done: configuration.rooms > 0 && configuration.tables > 0, view: 'bookings', tab: 'rooms' },
    { label: 'Imposta gli orari del servizio', done: configuration.shifts > 0, view: 'bookings', tab: 'shifts' },
  ];
  if (items.every((item) => item.done)) return null;
  return <section className="onboarding-card glass-card"><div><p className="eyebrow">PER INIZIARE</p><h2>Prepara la tua agenda</h2></div><div className="onboarding-items">{items.filter(item => !item.done).map((item) => <button key={item.label} onClick={() => onGo(item.view, item.tab)}><span>{item.label} →</span></button>)}</div></section>;
}

function StatusBadge({ enabled }) {
  return <span className={`status-badge ${enabled ? 'active' : 'pending'}`}><i />{enabled ? 'Prenotazioni online' : 'Link in pausa'}</span>;
}

function DirectLinkCard({ url, enabled, onOpenSettings }) {
  const [copied, setCopied] = useState('');
  async function copy(value, label = 'Link') {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(label); window.setTimeout(() => setCopied(''), 1800);
    } catch { setCopied('Copia non riuscita'); }
  }
  return <section className={`direct-link-card glass-card ${enabled ? '' : 'disabled'}`}><BrandMark className="direct-link-icon" /><div><span>IL TUO LINK PRENOTAZIONI</span><strong>{url.replace(/^https?:\/\//, '')}</strong><small>{enabled ? 'Pronto per sito, Instagram e Profilo Google' : 'Il link è temporaneamente in pausa'}</small></div><div className="direct-link-actions"><a className="button button-ghost button-small" href={url} target="_blank" rel="noreferrer">Apri ↗</a><button className="button button-small" onClick={() => copy(url, 'Copiato')}>{copied || 'Copia link'}</button><button className="link-settings" onClick={onOpenSettings} aria-label="Impostazioni link"><ProductIcon name="settings" size={20} /></button></div></section>;
}

function Overview({ metrics, averageCover, showRecover, callsCount = 0 }) {
  const maxDaily = Math.max(1, ...metrics.daily.map((item) => item.value));
  return <>
    <section className={`metric-grid ${showRecover ? '' : 'three'}`}>
      <Metric label="Prenotazioni" value={metrics.bookings} accent="violet" note="ricevute negli ultimi 30 giorni" />
      <Metric label="Coperti prenotati" value={metrics.covers} accent="coral" note={`media ${metrics.averageParty.toLocaleString('it-IT')} per tavolo`} />
      {showRecover && <Metric label="Da telefono" value={metrics.recovered} accent="green" note="prenotazioni attribuite al canale telefonico" />}
      {showRecover && <Metric label="Chiamate registrate" value={callsCount} accent="blue" note={callsCount >= 500 ? 'Vista limitata alle ultime 500' : 'nel gestionale, non tutto il traffico telefonico'} />}
    </section>
    <details className="rd-disclosure"><summary>Valore stimato dei coperti</summary><p>{metrics.value.toLocaleString('it-IT', { style: 'currency', currency: 'EUR' })} con una media impostata di {averageCover.toLocaleString('it-IT')} € a persona. È una stima delle prenotazioni nei dati caricati, non fatturato o incassi verificati.</p></details>
    <section className="app-grid-two">
      <article className="app-card glass-card chart-card"><div className="card-head"><div><p className="eyebrow">ULTIMI 7 GIORNI</p><h2>Prenotazioni confermate</h2></div><span>{metrics.weekBookings} totali</span></div><div className="real-chart">{metrics.daily.map((item) => <div className="real-chart-column" key={item.date}><b>{item.value || ''}</b><i style={{ height: `${Math.max(item.value ? 14 : 4, (item.value / maxDaily) * 100)}%` }} /><span>{item.label}</span></div>)}</div></article>
      <article className="app-card glass-card sources-card"><p className="eyebrow">PROVENIENZA</p><h2>Da dove prenotano</h2><div className="source-breakdown">{metrics.sources.map((source) => <div key={source.key}><span><i className={`source-dot ${source.key}`} />{source.label}</span><strong>{source.value}</strong></div>)}</div><p className="source-hint">Usa i link dedicati in Impostazioni per distinguere Google, Instagram e sito.</p></article>
    </section>
  </>;
}

function Metric({ label, value, note, accent }) { return <article className={`metric-card glass-card ${accent}`}><span>{label}</span><strong>{value}</strong><small>{note}</small></article>; }

function CallsView({ calls, settings, onActivate, onCheck }) {
  const active = settings.provisioning_status === 'active';
  return <><section className="activation-banner glass-card"><div><strong>{active ? 'Collegamento telefonico attivo' : settings.provisioning_status === 'requested' ? 'Collegamento in preparazione' : 'Collegamento da verificare'}</strong><p>{active ? 'Questa lista mostra le chiamate registrate dal gestionale, non tutto il traffico telefonico.' : 'L’attivazione del telefono è assistita: consulta lo stato e i documenti richiesti.'}</p></div><button className="button button-small" onClick={active ? onCheck : onActivate}>{active ? 'Verifica prenotazioni e SMS' : 'Vedi attivazione'}</button></section><CallsTable calls={calls} /></>;
}

function CallsTable({ calls }) {
  return <section className="app-card glass-card calls-card"><div className="card-head"><div><p className="eyebrow">ULTIMI 30 GIORNI</p><h2>Ultime chiamate</h2></div><span>{calls.length} registrate</span></div><p className="rd-view-hint">Gli importi sono stime del valore prenotato, non incassi. I minuti totali sono in “Piano e consumi”.</p>{calls.length ? <div className="calls-table">{calls.map((call) => <div className="call-row" key={call.id}><span className={`call-status ${call.status}`} /><div><strong>Numero ·•• {call.caller_phone_last4}</strong><small>{new Date(call.created_at).toLocaleString('it-IT', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</small></div><span>{statusLabel(call.status)}</span><b>{call.status === 'converted' && call.estimated_value != null ? `${Number(call.estimated_value).toLocaleString('it-IT')} €` : '—'}</b></div>)}</div> : <EmptyState title="Nessuna chiamata registrata" text="Qui compariranno le chiamate registrate dal gestionale e il loro esito." />}</section>;
}

function SettingsPanel({ settings, setSettings, saving, notice, bookingUrl, onSave, onActivate }) {
  const update = (key) => (event) => setSettings((current) => ({ ...current, [key]: event.target.value }));
  const canUseSms = planHas(settings.plan, 'sms');
  const campaignLinks = [{ label: 'Sito', value: `${bookingUrl}?src=website` }, { label: 'Instagram', value: `${bookingUrl}?src=instagram` }, { label: 'Google', value: `${bookingUrl}?src=google` }];
  const [copied, setCopied] = useState('');
  async function copy(value, label) {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(label); window.setTimeout(() => setCopied(''), 1600);
    } catch { setCopied('error'); }
  }
  return <section className="settings-layout">
    <article className="app-card glass-card settings-card">
      <h2>Prenotazioni online</h2>
      <label className="toggle-row"><div><strong>Link pubblico attivo</strong><small>Disattivalo per sospendere le nuove prenotazioni online.</small></div><input type="checkbox" checked={Boolean(settings.public_booking_enabled)} onChange={(event) => setSettings((current) => ({ ...current, public_booking_enabled: event.target.checked }))} /></label>
      <label className="settings-field">Messaggio nella pagina di prenotazione<textarea maxLength="300" rows="3" value={settings.public_booking_intro || ''} onChange={update('public_booking_intro')} placeholder="Prenota il tuo tavolo. Ti aspettiamo!" /></label>
      <details className="rd-disclosure"><summary>Regole di prenotazione</summary><p>Modifica questi limiti solo quando cambiano le regole del locale.</p><div className="native-settings-grid">
        <label className="settings-field">Persone massime per prenotazione<input type="number" min="1" max="50" value={settings.max_party_size} onChange={update('max_party_size')} /></label>
        <label className="settings-field">Durata del tavolo (minuti)<input type="number" min="30" max="360" step="15" value={settings.booking_duration_minutes} onChange={update('booking_duration_minutes')} /></label>
        <label className="settings-field">Preavviso per prenotare (minuti)<input type="number" min="0" max="10080" step="15" value={settings.min_notice_minutes} onChange={update('min_notice_minutes')} /></label>
        <label className="settings-field">Preavviso per modificare o annullare (minuti)<input type="number" min="0" max="10080" step="15" value={settings.cancellation_notice_minutes} onChange={update('cancellation_notice_minutes')} /></label>
        <label className="settings-field">Giorni prenotabili in anticipo<input type="number" min="1" max="365" value={settings.booking_window_days} onChange={update('booking_window_days')} /></label>
        <label className="settings-field">Spesa media stimata per persona (€)<input type="number" min="1" max="1000" value={settings.average_cover_value} onChange={update('average_cover_value')} /><small>Solo per le statistiche: non è un prezzo richiesto all’ospite.</small></label>
      </div></details>
      <details className="rd-disclosure"><summary>Messaggi di conferma</summary>
        <p>Le prenotazioni online ricevono una conferma email con il link di gestione.</p>
        {canUseSms && <label className="toggle-row"><div><strong>Conferme via SMS</strong><small>Per le prenotazioni telefoniche, dopo il consenso del cliente.</small></div><input type="checkbox" checked={Boolean(settings.sms_notifications_enabled)} onChange={(event) => setSettings((current) => ({ ...current, sms_notifications_enabled: event.target.checked }))} /></label>}
      </details>
      {notice && <div role="status" className={`form-alert ${notice.includes('Non siamo') ? 'error' : 'success'}`}>{notice}</div>}
      <div className="settings-actions"><button className="button" disabled={saving} onClick={onSave}>{saving ? 'Salvataggio…' : 'Salva impostazioni'}</button></div>
    </article>
    <aside className="app-card glass-card setup-card"><h2>Condividi il tuo calendario</h2><p>Usa il link adatto al canale: vedrai da dove arrivano le prenotazioni.</p>
      <div className="campaign-links">{campaignLinks.map((link) => <button key={link.label} disabled={!bookingUrl} onClick={() => copy(link.value, link.label)}><span>{link.label}</span><strong>{copied === link.label ? 'Copiato ✓' : copied === 'error' ? 'Riprova' : 'Copia link'}</strong></button>)}</div>
      {canUseSms && <div className="phone-next-step"><strong>{settings.provisioning_status === 'active' ? 'Telefono collegato' : 'Attivazione del telefono'}</strong><p>{settings.provisioning_status === 'active' ? 'Le impostazioni telefoniche si gestiscono con RistoDaily.' : 'Consulta lo stato del collegamento e i documenti richiesti.'}</p><button className="button button-ghost full" onClick={onActivate}>Vedi attivazione</button></div>}
    </aside>
  </section>;
}

function BillingPanel({ session, commerce, settings, onActivate }) {
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  async function open(path, body = {}) {
    setBusy(path); setError('');
    try {
      const response = await fetch('/api/billing', { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` }, body: JSON.stringify({ ...body, action: path }) });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error);
      window.location.assign(data.url);
    } catch (requestError) { setError(requestError.message || 'Operazione non riuscita'); setBusy(''); }
  }
  const subscription = commerce.subscription || { plan: 'free', status: 'manual' };
  const current = assignedPlan(settings);
  const usage = Number(settings.messages_used || 0);
  const allowance = Number(settings.message_allowance || 0);
  return <section className="billing-layout">
    <article className="app-card glass-card billing-current">
      <div><p className="eyebrow">PIANO ASSEGNATO</p><h2>{current.name}</h2>
        <p>{current.features.slice(0, 3).join(' · ')}</p>
        {current.minutes && <p>{current.minutes.toLocaleString('it-IT')} minuti inclusi al mese. Consumi e stato della lettura nel riepilogo qui sotto.</p>}
        <p>Attivazione assistita. Nessun nuovo addebito automatico; i contratti già esistenti restano separati.</p>
      </div>
      <div className="billing-current-actions">
        <span className="current-plan-status">{settings.provisioning_status === 'active' ? 'Servizio attivo' : 'Da attivare / verificare'}</span>
        {current.id !== 'free' && allowance > 0 && <div className="plan-usage"><span><b>{usage}</b> di {allowance} segmenti SMS usati</span><i><b style={{ width: `${Math.min(100, usage / allowance * 100)}%` }} /></i></div>}
        {subscription.status !== 'manual' && <button className="button button-ghost" disabled={Boolean(busy)} onClick={() => open('portal')}>Gestisci contratto esistente</button>}
      </div>
    </article>
    <UsageBillingPanel session={session} />
    <button className="button" onClick={onActivate}>{current.id === 'free' ? 'Richiedi assistente AI' : 'Stato attivazione e consegna'}</button>
    <details className="rd-disclosure"><summary>Confronta tutti i piani</summary>
      <div className="billing-plans">{Object.values(PLAN_CATALOG).map((plan) => <PlanCard key={plan.id} plan={plan} active={current.id === plan.id} />)}</div>
      <p>SMS conteggiati per segmento. Prezzo degli SMS extra e arrotondamenti dei minuti da definire prima dell’attivazione dei consumi. Nessun acquisto da questa pagina.</p>
    </details>
    {error && <div className="form-alert error billing-error">{error}</div>}
  </section>;
}

function PlanCard({ plan, active }) { return <article className={`app-card glass-card plan-card ${active ? 'active' : ''}`}><span>{active ? 'PIANO ASSEGNATO' : plan.id === 'free' ? 'SEMPRE GRATIS' : 'ATTIVAZIONE ASSISTITA'}</span><h3>{plan.name}</h3><strong>{plan.price}<small>{plan.priceSuffix}</small></strong><ul className="plan-feature-list">{plan.features.map((feature) => <li key={feature}><i>✓</i><span>{feature}</span></li>)}</ul><p>{active ? 'Piano assegnato, senza nuovo addebito' : plan.id === 'free' ? 'Calendario gratuito' : 'Assegnazione tramite RistoDaily · acquisto non attivo'}</p></article>; }

export function buildMetrics(reservations, averageCover, includeRecover = true) {
  const active = reservations.filter((item) => !['cancellata', 'no-show', 'no_show'].includes(item.status));
  const covers = active.reduce((sum, item) => sum + Number(item.party_size || 0), 0);
  const phoneBookings = active.filter(item => ['recover_sms', 'recover_voice_ai'].includes(item.source));
  const sourceConfig = [
    ['direct', 'Link diretto'], ['website', 'Sito'], ['instagram', 'Instagram'], ['google', 'Google'],
    ...(includeRecover ? [['recover_sms', 'Link da telefono'], ['recover_voice_ai', 'Assistente telefonico']] : []), ['manuale', 'Inserite dal ristorante'],
  ];
  const sources = sourceConfig.map(([key, label]) => ({ key, label, value: active.filter((item) => item.source === key).length })).filter((item) => item.value > 0);
  if (!sources.length) sources.push({ key: 'direct', label: 'Nessun dato ancora', value: 0 });
  const daily = [];
  for (let offset = 6; offset >= 0; offset -= 1) {
    const day = new Date(); day.setHours(12, 0, 0, 0); day.setDate(day.getDate() - offset);
    const iso = dateString(day);
    daily.push({ date: iso, label: day.toLocaleDateString('it-IT', { weekday: 'short' }).slice(0, 2), value: active.filter((item) => dateString(new Date(item.created_at)) === iso).length });
  }
  return {
    bookings: active.length,
    covers,
    averageParty: active.length ? Math.round((covers / active.length) * 10) / 10 : 0,
    value: covers * averageCover,
    recovered: phoneBookings.length,
    recoveredValue: phoneBookings.reduce((sum, booking) => sum + Number(booking.party_size || 0) * averageCover, 0),
    daily,
    weekBookings: daily.reduce((sum, item) => sum + item.value, 0),
    sources,
  };
}

function statusLabel(status) { return ({ converted: 'Prenotazione', link_opened: 'Link aperto', link_sent: 'SMS inviato', consent_accepted: 'Consenso', failed: 'Non riuscita' })[status] || 'Chiamata registrata'; }
function EmptyState({ title, text }) { return <div className="empty-state"><span>↗</span><h3>{title}</h3><p>{text}</p></div>; }
